import React, { useState, useEffect, useCallback } from 'react';
import './StupidServices.css';

const DEFAULT_BATCH = 20;
const LOAD_MORE_THRESHOLD = 300;

const formatPrice = (value) => {
    return Number(value).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    });
};

const parseNumericPrice = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const match = String(value ?? '').match(/\d[\d,]*(?:\.\d+)?/);
    if (!match) return 0;
    return Number(match[0].replace(/,/g, '')) || 0;
};

function StupidServices({ theme, onBackClick, onAddToCart }) {
    const [entries, setEntries] = useState([]);
    const [visibleCount, setVisibleCount] = useState(DEFAULT_BATCH);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [quantityById, setQuantityById] = useState({});
    const [cartMessage, setCartMessage] = useState('');
    const [justAddedId, setJustAddedId] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const loadEntries = async () => {
            try {
                const response = await fetch('/stupid-services.json');
                if (!response.ok) {
                    throw new Error(`Unable to load content: ${response.status}`);
                }
                const data = await response.json();

                if (!cancelled) {
                    const flattenedEntries = [];
                    let itemCounter = 1;

                    // 1. Process Categories & Services (These get the incrementing counter)
                    if (data?.catalog?.categories) {
                        data.catalog.categories.forEach(category => {
                            flattenedEntries.push({ type: 'section', text: category.name });
                            category.services.forEach(service => {
                                flattenedEntries.push({
                                    type: 'service',
                                    itemIndex: itemCounter++,
                                    ...service
                                });
                            });
                        });
                    }

                    // 2. Process Small Print (Separated from the counter entirely)
                    if (data?.catalog?.smallPrint) {
                        flattenedEntries.push({ type: 'section', text: 'NOTES:' });
                        data.catalog.smallPrint.forEach(print => {
                            flattenedEntries.push({
                                type: 'smallprint',
                                text: print
                            });
                        });
                    }

                    setEntries(flattenedEntries);
                    setVisibleCount(DEFAULT_BATCH);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error(err);
                    setError('Could not load services. Our "I Will Break Things" specialist might have touched the server.');
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        loadEntries();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleScroll = useCallback(() => {
        if (isLoading || entries.length <= visibleCount) return;
        if (window.innerHeight + window.scrollY >= document.body.offsetHeight - LOAD_MORE_THRESHOLD) {
            setVisibleCount((prev) => Math.min(entries.length, prev + DEFAULT_BATCH));
        }
    }, [entries.length, isLoading, visibleCount]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const visibleEntries = entries.slice(0, visibleCount);

    const handleAddToCart = (entry, quantity) => {
        if (!onAddToCart) return;

        const normalizedQuantity = Math.max(1, Number(quantity) || 1);
        const product = {
            id: `service-${entry.itemIndex ?? entry.title}`,
            name: entry.title,
            desc: entry.description || 'A premium useless service for your friction-filled future.',
            img: '',
            price: parseNumericPrice(entry.price),
        };

        onAddToCart(product, normalizedQuantity);
        setQuantityById((prev) => ({ ...prev, [String(product.id)]: 1 }));
        setJustAddedId(String(product.id));
        setCartMessage(`Added ${normalizedQuantity} × ${entry.title} to the cart.`);
        window.setTimeout(() => {
            setJustAddedId(null);
            setCartMessage('');
        }, 1400);
    };

    return (
        <div className={`app ${theme}`}>
            <main>
                <section className="services-header-section">
                    <div className="services-header">
                        <div>
                            <h1>Gloriously Useless Services</h1>
                            <p className="services-subtitle">
                                Premium services you don't need, at prices you shouldn't pay. Results not guaranteed.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>
                            Back to Home
                        </button>
                    </div>
                </section>

                {cartMessage && (
                    <div className="cart-feedback">{cartMessage}</div>
                )}

                <section className="services-content">
                    {isLoading ? (
                        <div className="services-status">Loading unnecessary services…</div>
                    ) : error ? (
                        <div className="services-status services-error">{error}</div>
                    ) : (
                        <>
                            <div className="services-list">
                                {visibleEntries.map((entry, index) => {
                                    if (entry.type === 'section') {
                                        return (
                                            <div key={`section-${index}`} className="services-section-heading">
                                                <h2>{entry.text}</h2>
                                            </div>
                                        );
                                    }

                                    if (entry.type === 'service') {
                                        const itemKey = `service-${entry.itemIndex ?? index}`;
                                        const quantityValue = quantityById[itemKey] ?? 1;

                                        return (
                                            <article key={`entry-${index}`} className="services-card">
                                                <div className="services-index">#{entry.itemIndex}</div>
                                                <div className="services-card-content">
                                                    <div className="services-card-header">
                                                        <h3>{entry.title}</h3>
                                                        <span className="services-price">{entry.price}</span>
                                                    </div>
                                                    {entry.description && (
                                                        <p className="services-description">{entry.description}</p>
                                                    )}
                                                    <div className="product-footer">
                                                        <span className="product-price">{formatPrice(parseNumericPrice(entry.price))}</span>
                                                        <div className="product-actions">
                                                            <label className="qty-label">
                                                                Qty
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    value={quantityValue}
                                                                    onChange={(event) => {
                                                                        const nextValue = Math.max(1, Number(event.target.value) || 1);
                                                                        setQuantityById((prev) => ({ ...prev, [itemKey]: nextValue }));
                                                                    }}
                                                                />
                                                            </label>
                                                            <button
                                                                type="button"
                                                                className={`add-cart-btn ${justAddedId === String(`service-${entry.itemIndex ?? index}`) ? 'added' : ''}`}
                                                                onClick={() => handleAddToCart(entry, quantityValue)}
                                                            >
                                                                {justAddedId === String(`service-${entry.itemIndex ?? index}`) ? 'Added to cart' : 'Add to Cart'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </article>
                                        );
                                    }

                                    if (entry.type === 'smallprint') {
                                        return (
                                            <article key={`entry-${index}`} className="services-card smallprint">
                                                <div className="services-card-content">
                                                    <p>{entry.text}</p>
                                                </div>
                                            </article>
                                        );
                                    }

                                    return null;
                                })}
                            </div>

                            <div className="services-footer-note">
                                {visibleCount < entries.length
                                    ? 'Scroll to load more ways to waste money...'
                                    : 'You have reached the end. Please buy something now.'}
                            </div>

                            <button className="back-btn" onClick={onBackClick}>
                                Back to Home
                            </button>
                        </>
                    )}
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default StupidServices;